import React, { useState, useEffect } from "react";
// import Button from 'react-bootstrap/Button';
import Modal from "react-bootstrap/Modal";
import { Button, TextField, Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import config from "../config";
import { DAPP_ICON, TransactionName } from "../constants";
import ConfirmModal from "./ConfirmPopup";
import { ExecuteSubmitTransaction, formattedErrorMessage } from "../utils";
import MyVerticallyCenteredModal from "./MyVerticallyCenteredModal";
import BigNumber from "bignumber.js";

const getErrorMessage = (message) => {
  try {
    // Remove "Error:" prefix if it exists
    if (message.startsWith("Error:")) {
      message = message.replace("Error: ", "");
    }

    // Try to parse as JSON first (for structured errors)
    if (message.startsWith("{")) {
      const parsedError = JSON.parse(message);

      // Check for specific error message and return custom messages
      if (parsedError?.message?.error === "insufficient balance") {
        return "Not Authorized!";
      }

      // Return the error message from the parsed object if available
      return parsedError.message?.error || "Unknown Error";
    }

    // Handle plain text errors
    if (message === "insufficient balance") {
      return "Not Authorized!";
    }

    // If no mapping, return the original message
    return message;
  } catch (err) {
    // If parsing fails, return the original message or a fallback
    return message || "An unexpected error occurred.";
  }
};

const Send = ({ balance, setBalance, address, isConnected }) => {
  var dappToken = "";
  const [receiverAddress, setReceiverAddress] = useState("");
  const [amount, setAmount] = useState(new BigNumber(0));
  const [responseMessage, setResponseMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({}); // To store validation errors
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  console.log(address, "address");

  useEffect(() => {
    if (isConnected) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [isConnected]);

  const handleSend = async () => {
    setConfirmPopupOpen(false);
    let validationErrors = {};

    // Validate amount
    if (!amount || new BigNumber(amount).isLessThanOrEqualTo(0)) {
      validationErrors.amount = "Amount must be greater than 0";
    }

    // Validate receiver address
    if (!receiverAddress) {
      validationErrors.receiverAddress = "Receiver's address is required";
    }

    // If there are errors, set the errors state and prevent transaction
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear any existing errors before proceeding
    setErrors({});

    try {
      const transactionParams = [
        receiverAddress,
        new BigNumber(amount).multipliedBy(100).toFixed(0),
      ];
      const [res, errMsg] = await ExecuteSubmitTransaction(
        config.CHANNEL_NAME,
        config.CHAINCODE_NAME,
        TransactionName.TRANSFER,
        transactionParams
      );
      if (res !== undefined) {
        setResponseMessage(
          res !== undefined ? `${res}` : `Error: ${formattedErrorMessage(res)}`
        );
        if (res !== undefined) {
          setReceiverAddress("");
          setBalance(new BigNumber(balance).minus(amount));
          setAmount(new BigNumber(0));
        } else {
          try {
            // Attempt to parse the error message if it's a stringified JSON
            const errorObj = JSON.parse(res);
            setResponseMessage(
              `Error: ${formattedErrorMessage(errorObj.message.error)}`
            );
          } catch (parseError) {
            // If parsing fails, show the raw error
            setResponseMessage(`Error: ${formattedErrorMessage(res)}`);
          }
        }
      } else {
        throw new Error(formattedErrorMessage(errMsg));
      }
    } catch (error) {
      setResponseMessage("Error: An error occurred during the transaction.");
    } finally {
      setModalShow(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "20px",
        textAlign: "center",
        backgroundColor: "#f2f4f6",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
          borderRadius: "10px",
          width: "100%", // This ensures it will align with the button width
          maxWidth: "600px", // Ensure it's aligned with the button width
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#7A7A7A", marginBottom: "10px" }}
        >
          Send
        </Typography>
        {/* Toggle between Typography and input */}
        {isEditing ? (
          <input
            value={amount}
            type="text"
            onChange={(e) => {
              const regex = /^\d{0,20}(\.\d{0,2})?$/;
              const value = e.target.value
                .replace(/[^0-9.]/g, "") // Remove non-numeric characters except "."
                .replace(/(\..*?)\..*/g, "$1") // Allow only one decimal point
                .replace(/^(\d+\.\d{2})\d+$/, "$1") // Limit to 2 decimal places
                .replace(/^0([0-9]+)/, "$1"); // Remove leading zeros
              if (regex.test(value)) {
                setAmount(value);
              }
            }}
            onBlur={() => {
              if (amount === "") {
                setAmount(new BigNumber(0).toFixed());
              } else {
                setAmount(new BigNumber(amount).toFixed());
              }
              setIsEditing(false);
            }}
            autoFocus
            style={{
              fontWeight: "bold",
              fontSize: "32px",
              textAlign: "center",
              marginBottom: "10px",
              color: "black",
              border: "none",
              outline: "none",
              appearance: "textfield", // Removes the number input arrows
              borderColor: errors.amount ? "red" : "", // Add red border on error
            }}
          />
        ) : (
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              marginBottom: "10px",
              color:
                !isNaN(Number(balance)) && Number(balance) && isConnected
                  ? "black"
                  : "#9E9E9E",
              cursor:
                !isNaN(Number(balance)) && Number(balance) && isConnected
                  ? "pointer"
                  : "",
              whiteSpace: "nowrap", // Prevent text wrapping
              overflow: "hidden", // Hide overflowing content
              textOverflow: "ellipsis", // Add ellipsis if content overflows
              fontSize: "clamp(12px, 5vw, 36px)", // Dynamically scale font size based on viewport and container size
              textAlign: "center", // Align text to the center
              maxWidth: "100%", // Ensure it doesn't exceed its container width
            }}
            onClick={() => {
              if (
                isConnected &&
                !isNaN(Number(balance)) &&
                Number(balance) > 0
              ) {
                setIsEditing(true);
              }
            }}
            // Enter edit mode on click
          >
            ₹{new BigNumber(amount).toFormat(2)}
          </Typography>
        )}
        {errors.amount && (
          <Typography variant="body2" sx={{ color: "red" }}>
            {errors.amount}
          </Typography>
        )}{" "}
        {/* Show error */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "10px",
          }}
        >
          <img
            src={DAPP_ICON} // Replace with actual CBDC image
            alt="CBDC Icon"
            style={{ width: "20px", height: "20px", marginRight: "5px" }}
          />
          <Typography variant="body1" sx={{ color: "#7A7A7A" }}>
            CBDC
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <Button
            variant="outlined"
            disabled={
              !isConnected || isNaN(Number(balance)) || Number(balance) <= 0
            }
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              marginRight: "10px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(100)}
          >
            ₹100
          </Button>
          <Button
            variant="outlined"
            disabled={
              !isConnected || isNaN(Number(balance)) || Number(balance) <= 0
            }
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              marginRight: "10px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(250)}
          >
            ₹250
          </Button>
          <Button
            variant="outlined"
            disabled={
              !isConnected || isNaN(Number(balance)) || Number(balance) <= 0
            }
            sx={{
              borderRadius: "10px",
              minWidth: "60px",
              color: "#4C2CF5",
              borderColor: "#4C2CF5",
              marginBottom: "-10px",
              "&:hover": { backgroundColor: "#F0F0F0" },
            }}
            onClick={() => setAmount(500)}
          >
            ₹500
          </Button>
        </Box>
      </Box>
      {isConnected && (
        <Typography
          variant="body2"
          sx={{
            marginBottom: 2,
            cursor: "pointer",
            marginTop: 2,
          }}
        >
          Balance:
          <Box
            component="span"
            sx={{
              color: "#1939B7",
              fontWeight: "bold",
              marginLeft: 1,
            }}
          >
            {loading
              ? "Loading"
              : !loading &&
                isConnected &&
                !isNaN(Number(balance)) &&
                Number(balance) > 0
              ? `₹${new BigNumber(balance).toFormat(2)}`
              : `₹0.00`}
          </Box>
        </Typography>
      )}
      <TextField
        placeholder="Enter Receiver's Address"
        value={receiverAddress}
        onChange={(e) => {
          let validationErrors = {};
          setReceiverAddress(e.target.value.toLowerCase());
          if (address === e.target.value) {
            validationErrors.receiverAddress =
              "transfer to self is not allowed, please enter different address";
          }
          setErrors(validationErrors);
        }}
        variant="outlined"
        fullWidth
        sx={{
          textTransform: "lowercase",
          marginTop: isConnected ? "" : "10px",
          backgroundColor: "white",
          borderRadius: "8px",
          marginBottom: "20px",
          maxWidth: "600px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "& fieldset": {
              borderColor: errors.receiverAddress ? "red" : "#E0E0E0", // Add red border on error,
            },
            "&:hover fieldset": {
              borderColor: errors.receiverAddress ? "red" : "#E0E0E0",
            },
            "&.Mui-focused fieldset": {
              borderColor: errors.receiverAddress ? "red" : "#1976D2",
            },
          },
        }}
      />
      {errors.receiverAddress && (
        <Typography variant="body2" sx={{ color: "red" }}>
          {errors.receiverAddress}
        </Typography>
      )}{" "}
      {/* Show error */}
      <Button
        variant="contained"
        fullWidth
        disabled={
          receiverAddress === "" ||
          amount == 0 ||
          receiverAddress?.length !== 40 ||
          Object.keys(errors).length > 0 ||
          new BigNumber(amount).isGreaterThan(new BigNumber(balance))
        }
        onClick={() => {
          setConfirmPopupOpen(true);
        }}
        sx={{
          backgroundColor: "#0055FF",
          borderRadius: "8px",
          height: "45px",
          maxWidth: "600px", // Ensure this width matches the box width
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "bold",
          marginTop: "20px",
          "&:hover": {
            backgroundColor: "#0044CC",
          },
        }}
      >
        Send
      </Button>
      <ConfirmModal
        open={confirmPopupOpen}
        setOpen={setConfirmPopupOpen}
        onConfirm={handleSend}
      />
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />
    </Box>
  );
};

export default Send;
